import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useField } from "formik";

import axios from "axios";
import {
  indexTextItemForSearch,
  matchTextItemWithQuery,
  getCachedTransliteration,
  fetchTransliteration,
} from "../helpers/tamilPhonetics";

const STORAGE_CACHE_KEY = "dwt_consolidated_villages";

/**
 * ConsolidatedVillageInput
 * Autocomplete combobox showing consolidated list of villages from the DB.
 * Live filtering supporting phonetic Tanglish & Tamil search.
 * Keyboard accessible (↑↓ navigate, ↵ select, esc close).
 * Auto-advances focus to nextRef (Area input) upon selection.
 */
const ConsolidatedVillageInput = ({
  id = "village",
  name = "village",
  placeholder = "Village name",
  className = "",
  innerRef,
  nextRef,
  moiList = [],
  apiGet = null,
  onSelect = null,
  ...props
}) => {
  const [field, , helpers] = useField(name);


  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [apiSuggestions, setApiSuggestions] = useState([]);

  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Helper to get auth token
  const getAuthToken = useCallback(() => {
    return (
      localStorage.getItem("dwt-entry-tok") ||
      localStorage.getItem("dwt-tok") ||
      localStorage.getItem("dwt-cust-tok") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("org_access_token") ||
      localStorage.getItem("token") ||
      ""
    );
  }, []);

  // Fetch consolidated villages from DB (with cache fallback)
  useEffect(() => {
    let isMounted = true;

    // 1. Initial hydrate from local storage
    try {
      const cached = JSON.parse(localStorage.getItem(STORAGE_CACHE_KEY) || "[]");
      if (Array.isArray(cached) && cached.length > 0 && isMounted) {
        setVillages(cached);
      }
    } catch (e) {}

    const loadVillages = async () => {
      setLoading(true);
      try {
        let remoteList = [];
        if (typeof apiGet === "function") {
          const res = await apiGet("entry/villages");
          if (res?.data?.data && Array.isArray(res.data.data)) {
            remoteList = res.data.data;
          }
        } else {
          const token = getAuthToken();
          const rawBaseUrl =
            process.env.REACT_APP_API_URL ||
            process.env.REACT_APP_BACKEND_URL ||
            "http://localhost:8000";
          const baseUrl = rawBaseUrl.replace(/\/+$/, "");
          const res = await axios.get(`${baseUrl}/entry/villages`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.data?.data && Array.isArray(res.data.data)) {
            remoteList = res.data.data;
          }
        }

        // Also harvest villages from local moiList
        const localVillages = (moiList || [])
          .map((m) => (m.village || "").trim())
          .filter(Boolean)
          .map((v) => ({ village: v, village_en: "" }));

        // Deduplicate
        const merged = [...remoteList, ...localVillages];
        const unique = [];
        const seen = new Set();
        for (const item of merged) {
          const v = typeof item === "string" ? item.trim() : (item.village || "").trim();
          const en = typeof item === "object" ? (item.village_en || "").trim() : "";
          const lower = v.toLowerCase();
          if (v && !seen.has(lower)) {
            seen.add(lower);
            unique.push({ village: v, village_en: en });
          }
        }

        if (unique.length > 0 && isMounted) {
          unique.sort((a, b) => a.village.localeCompare(b.village, "ta"));
          setVillages(unique);
          try {
            localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify(unique));
          } catch (e) {}
        }
      } catch (err) {
        console.warn("Failed to fetch consolidated villages, using local cache:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadVillages();

    return () => {
      isMounted = false;
    };
  }, [apiGet, getAuthToken, moiList]);

  // Pre-index villages for O(1) phonetic Tanglish and Tamil searching
  const indexedVillages = useMemo(() => {
    return (villages || []).map((v) => indexTextItemForSearch(v));
  }, [villages]);

  // Debounced API transliteration for English search queries
  useEffect(() => {
    const raw = (field.value || "").trim();
    if (!raw || !/[a-zA-Z]/.test(raw)) {
      setApiSuggestions([]);
      return;
    }

    const cached = getCachedTransliteration(raw);
    if (cached) {
      setApiSuggestions(cached);
      return;
    }

    const timer = setTimeout(async () => {
      const suggestions = await fetchTransliteration(raw);
      setApiSuggestions(suggestions);
    }, 200);

    return () => clearTimeout(timer);
  }, [field.value]);

  // Filter villages based on current input
  const filteredVillages = useMemo(() => {
    const query = (field.value || "").trim();
    if (!query) {
      return indexedVillages;
    }
    return indexedVillages.filter((v) =>
      matchTextItemWithQuery(v, query, apiSuggestions)
    );
  }, [field.value, indexedVillages, apiSuggestions]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Suppress transliteration popup when dropdown is visible
  useEffect(() => {
    const inputEl = innerRef?.current || wrapperRef.current?.querySelector("input");
    if (!inputEl) return;

    if (showDropdown && filteredVillages.length > 0) {
      inputEl.dataset.suppressTranslate = "true";
      inputEl.classList.add("suppress-translate");
      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
    } else {
      const wasSuppressed = inputEl.dataset.suppressTranslate === "true";
      inputEl.dataset.suppressTranslate = "false";
      inputEl.classList.remove("suppress-translate");
      if (wasSuppressed && /[a-zA-Z]/.test(inputEl.value || "")) {
        inputEl.dispatchEvent(
          new CustomEvent("request-tamil-suggestion", { bubbles: true })
        );
      }
    }
  }, [showDropdown, filteredVillages.length, innerRef]);

  // Auto-scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedEl = dropdownRef.current.querySelector(
        `[data-village-index="${highlightedIndex}"]`
      );
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  // Handle Village selection
  const handleSelectVillage = useCallback(
    (villageItem) => {
      const selectedVillage = villageItem.raw || villageItem.village || "";
      helpers.setValue(selectedVillage);
      setShowDropdown(false);
      setHighlightedIndex(-1);

      document.dispatchEvent(new CustomEvent("dismiss-tamil-suggestions"));
      const inputEl = innerRef?.current || wrapperRef.current?.querySelector("input");
      if (inputEl) {
        inputEl.dataset.suppressTranslate = "false";
        inputEl.classList.remove("suppress-translate");
      }

      if (typeof onSelect === "function") {
        onSelect(selectedVillage);
      }

      // Smoothly advance focus to Area input
      setTimeout(() => {
        nextRef?.current?.focus();
      }, 50);
    },
    [helpers, innerRef, nextRef, onSelect]
  );

  // Key navigation
  const handleKeyDown = (e) => {
    if (showDropdown && filteredVillages.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredVillages.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredVillages.length - 1
        );
        return;
      }
      if (e.key === "Enter") {
        if (highlightedIndex >= 0 && filteredVillages[highlightedIndex]) {
          e.preventDefault();
          e.stopPropagation();
          handleSelectVillage(filteredVillages[highlightedIndex]);
          return;
        } else {
          // If no item is highlighted, user pressed enter with typed text
          setShowDropdown(false);
          setHighlightedIndex(-1);
          if (nextRef?.current) {
            e.preventDefault();
            e.stopPropagation();
            nextRef.current.focus();
            return;
          }
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        return;
      }
    } else {
      if (e.key === "Enter") {
        if (nextRef?.current) {
          e.preventDefault();
          e.stopPropagation();
          nextRef.current.focus();
        }
      }
    }
  };

  const isVisible = showDropdown && filteredVillages.length > 0;

  return (
    <div
      ref={wrapperRef}
      className="relative w-full consolidated-village-combobox"
      onFocusCapture={() => {
        if (villages.length > 0) {
          setShowDropdown(true);
        }
      }}
      onInputCapture={() => {
        if (villages.length > 0) {
          setShowDropdown(true);
        }
      }}
    >
      <input
        {...field}
        {...props}
        ref={innerRef}
        id={id}
        name={name}
        type="text"
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-expanded={isVisible}
        role="combobox"
        aria-controls={isVisible ? "consolidated-village-listbox" : undefined}
      />

      {isVisible && (
        <div
          ref={dropdownRef}
          role="listbox"
          id="consolidated-village-listbox"
          className="absolute z-50 left-0 top-full mt-1.5 w-full min-w-[280px] sm:min-w-[340px] max-h-72 bg-white/95 backdrop-blur-md rounded-2xl border-2 border-indigo-200 shadow-2xl overflow-hidden flex flex-col animation-in fade-in slide-in-from-top-1 duration-150"
        >
          {/* Header Bar */}
          <div className="bg-gradient-to-r from-indigo-50 to-indigo-100/70 px-3 py-2 border-b border-indigo-100 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-indigo-900 font-bold truncate">
              <span className="text-sm">🏘️</span>
              <span>Consolidated Villages</span>
              {loading ? (
                <span className="inline-block w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin shrink-0" />
              ) : (
                <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-extrabold shrink-0">
                  {filteredVillages.length}
                </span>
              )}
            </div>
            <span className="text-[10px] text-indigo-500 font-medium hidden sm:inline shrink-0">
              ↑↓ navigate • ↵ select • esc close
            </span>
          </div>

          {/* Villages List */}
          <div className="overflow-y-auto max-h-60 divide-y divide-gray-100 py-1">
            {filteredVillages.map((item, idx) => {
              const isHighlighted = idx === highlightedIndex;
              const villageName = item.raw || item.village || "";
              const alias = item.alias || item.village_en || "";

              return (
                <div
                  key={`${villageName}-${idx}`}
                  data-village-index={idx}
                  role="option"
                  aria-selected={isHighlighted}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectVillage(item);
                  }}
                  className={`px-3 py-2 cursor-pointer transition-all flex items-center justify-between gap-2 select-none ${
                    isHighlighted
                      ? "bg-indigo-50 border-l-4 border-indigo-600 pl-2.5 shadow-sm"
                      : "hover:bg-gray-50/80"
                  }`}
                >
                  <span className="text-sm font-bold text-gray-900 truncate">
                    {villageName}
                  </span>
                  {alias && (
                    <span className="text-[11px] font-mono text-gray-400 capitalize truncate shrink-0">
                      {alias}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsolidatedVillageInput;
